/**
 * Right to erasure — the two-step confirmation.
 *
 * TWO STEPS, because this is the only irreversible action in MeshHub and it
 * reaches four services. A single «مطمئنید؟» is the confirmation people click
 * through without reading; typing the contact's own surname is the smallest
 * ceremony that makes a misclick impossible.
 *
 * The first step lists **what will be cleared** and **which services will
 * change**, and both lists come from the server. Retyping them in Persian here
 * would be a promise this dialog makes that the code might not keep — and the
 * whole point of naming them is that the person clicking can see the blast
 * radius while it is still cancellable.
 */
import { Badge, Button } from '@meshhub/design-tokens/ui';
import { useMutation } from '@apollo/client';
import { AlertTriangle } from 'lucide-react';
import React, { useState } from 'react';
import { FormDialog, MutationError, TextField } from '../FormKit.web.jsx';
import { ERASE_CONTACT } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

//: The field names the server reports, in Persian. Kept here rather than
//: server-side because this is presentation, and an unknown key falls back to
//: the raw name rather than vanishing — a field we forgot to translate must
//: still be listed, or the dialog under-promises what it destroys.
const FIELD_LABELS = {
  first_name: 'نام',
  last_name: 'نام خانوادگی',
  org_name: 'نام سازمان',
  birth_date: 'تاریخ تولد',
  gender: 'جنسیت',
  address: 'نشانی',
  instagram_id: 'اینستاگرام',
  description: 'توضیحات',
  extra_attributes: 'فیلدهای سفارشی',
  photo_attachment_id: 'عکس',
  phone_number: 'شماره‌های تماس',
};

const SERVICE_LABELS = {
  scheduling: 'تقویم و فعالیت‌ها',
  messaging: 'پیام‌ها',
  feedback: 'ایشوها',
  analytics: 'داشبورد',
};

export function EraseDialog({ open, onOpenChange, contact, onErased }) {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState(null);

  const [erase, state] = useMutation(ERASE_CONTACT, {
    onCompleted: (d) => {
      setResult(d.eraseContact);
      setStep(3);
      onErased?.(d.eraseContact);
    },
  });

  // The surname is what must be typed. `displayName` would include an
  // honorific a user never thinks of as part of the name.
  const expected = (contact?.lastName ?? contact?.displayName ?? '').trim();
  const matches = typed.trim() === expected && expected.length > 0;

  function close(next) {
    if (!next) {
      setStep(1);
      setTyped('');
      setReason('');
      setResult(null);
    }
    onOpenChange(next);
  }

  if (step === 3 && result) {
    return (
      <FormDialog open={open} onOpenChange={close} title={S.erasure.done}>
        <p className="text-sm text-text">{S.erasure.done}</p>
        <ul className="mt-2 flex flex-wrap gap-1">
          {result.downstreamServices.map((s) => (
            <Badge key={s} className="bg-surface-hover text-text-muted">
              {SERVICE_LABELS[s] ?? s}
            </Badge>
          ))}
        </ul>
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={close}
      title={step === 1 ? S.erasure.step1Title : S.erasure.step2Title}
      submitLabel={step === 1 ? S.app.next : S.erasure.confirm}
      busy={state.loading}
      onSubmit={
        step === 1
          ? () => setStep(2)
          : () =>
              matches &&
              erase({ variables: { contactId: contact.id, reason: reason.trim() } })
      }
    >
      <div className="flex items-start gap-2 rounded border border-warning bg-warning-subtle p-3">
        <AlertTriangle size={16} className="mt-px shrink-0 text-warning" aria-hidden="true" />
        <p className="text-sm text-text">{S.erasure.step1Body}</p>
      </div>

      {step === 1 && (
        <>
          <section className="mt-3">
            <h3 className="text-sm font-medium text-text">{S.erasure.step1Fields}</h3>
            <ul className="mt-1 flex flex-wrap gap-1">
              {(contact?.erasureFields ?? Object.keys(FIELD_LABELS)).map((f) => (
                <Badge key={f} className="bg-surface-hover text-text-muted">
                  {FIELD_LABELS[f] ?? f}
                </Badge>
              ))}
            </ul>
          </section>
          <section className="mt-3">
            <h3 className="text-sm font-medium text-text">{S.erasure.step1Services}</h3>
            <ul className="mt-1 flex flex-wrap gap-1">
              {Object.entries(SERVICE_LABELS).map(([key, label]) => (
                <Badge key={key} className="bg-surface-hover text-text-muted">
                  {label}
                </Badge>
              ))}
            </ul>
          </section>
          <TextField
            label={S.erasure.reason}
            hint={S.erasure.reasonHint}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </>
      )}

      {step === 2 && (
        <>
          <TextField
            label={`${S.erasure.step2Title}: «${expected}»`}
            hint={S.erasure.step2Hint}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            error={typed && !matches ? S.erasure.mismatch : undefined}
            autoFocus
          />
          <MutationError error={state.error} />
          <Button variant="ghost" onClick={() => setStep(1)} type="button">
            {S.erasure.back}
          </Button>
        </>
      )}
    </FormDialog>
  );
}
